import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client.js'

type SubView = 'vehicles' | 'customers' | 'routes' | 'brokers'

const SUB_NAV: Array<{ key: SubView; label: string }> = [
  { key: 'vehicles', label: 'By Vehicle' },
  { key: 'customers', label: 'By Customer' },
  { key: 'routes', label: 'By Route' },
  { key: 'brokers', label: 'By Broker' },
]

/**
 * Stage 10 §3.5: a tab selector across the four rollups, each a sortable
 * table, with a shared period filter at the top — changing it re-fetches
 * every tab rather than filtering client-side, since the rollups are
 * re-aggregated server-side per period (Stage 10 §7 test: "recalculates
 * correctly without needing a page reload").
 */
export function ProfitabilityPage() {
  const [sub, setSub] = useState<SubView>('vehicles')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  return (
    <div>
      <nav className="tf-tabs" style={{ marginBottom: '1rem' }}>
        {SUB_NAV.map((item) => (
          <button
            key={item.key}
            type="button"
            className={sub === item.key ? 'tf-tab-active' : ''}
            onClick={() => setSub(item.key)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <label className="tf-field">
          <span>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="tf-field">
          <span>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>

      {sub === 'vehicles' && <RollupTable endpoint="vehicles" from={from} to={to} nameKey="vehicle_registration_no_normalized" nameLabel="Vehicle" extraColumns={[]} />}
      {sub === 'customers' && <RollupTable endpoint="customers" from={from} to={to} nameKey="customer_name" nameLabel="Customer" extraColumns={[]} />}
      {sub === 'routes' && (
        <RollupTable
          endpoint="routes"
          from={from}
          to={to}
          nameKey="origin_sample"
          nameLabel="Route"
          extraColumns={[]}
          renderName={(r) => `${r.origin_sample} → ${r.destination_sample}`}
        />
      )}
      {sub === 'brokers' && (
        <RollupTable
          endpoint="brokers"
          from={from}
          to={to}
          nameKey="broker_name"
          nameLabel="Broker"
          extraColumns={[
            { key: 'total_hire_cost', label: 'Hire Cost' },
            { key: 'total_brokerage', label: 'Brokerage' },
          ]}
        />
      )}
    </div>
  )
}

interface RollupRow {
  [key: string]: unknown
  trip_count: number
  total_revenue: string
  total_expense: string
  net_profit: string
  margin_pct: string | null
  final_trip_count: number
}

function RollupTable({
  endpoint,
  from,
  to,
  nameKey,
  nameLabel,
  extraColumns,
  renderName,
}: {
  endpoint: string
  from: string
  to: string
  nameKey: string
  nameLabel: string
  extraColumns: { key: string; label: string }[]
  renderName?: (row: RollupRow) => string
}) {
  const [rows, setRows] = useState<RollupRow[]>([])
  const [sortKey, setSortKey] = useState('net_profit')
  const [sortDesc, setSortDesc] = useState(true)

  useEffect(() => {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    const qs = params.toString()
    void api.get<RollupRow[]>(`/profitability/${endpoint}${qs ? `?${qs}` : ''}`).then(setRows)
  }, [endpoint, from, to])

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = Number(a[sortKey] ?? 0)
      const bv = Number(b[sortKey] ?? 0)
      return sortDesc ? bv - av : av - bv
    })
    return copy
  }, [rows, sortKey, sortDesc])

  function toggleSort(key: string) {
    if (key === sortKey) setSortDesc((d) => !d)
    else {
      setSortKey(key)
      setSortDesc(true)
    }
  }

  const columns = [
    { key: 'trip_count', label: 'Trips' },
    { key: 'total_revenue', label: 'Revenue' },
    { key: 'total_expense', label: 'Expense' },
    { key: 'net_profit', label: 'Net Profit' },
    ...extraColumns,
    { key: 'margin_pct', label: 'Margin %' },
    { key: 'final_trip_count', label: 'Final Trips' },
  ]

  if (!sorted.length) return <p className="tf-note">No trips in this period.</p>

  return (
    <table className="tf-table">
      <thead>
        <tr>
          <th>{nameLabel}</th>
          {columns.map((c) => (
            <th key={c.key} onClick={() => toggleSort(c.key)} style={{ cursor: 'pointer' }}>
              {c.label} {sortKey === c.key && (sortDesc ? '▼' : '▲')}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row, i) => (
          <tr key={i}>
            <td>{renderName ? renderName(row) : String(row[nameKey] ?? '—')}</td>
            {columns.map((c) => (
              <td key={c.key}>{c.key === 'margin_pct' ? (row[c.key] != null ? `${row[c.key]}%` : '—') : c.key.includes('trip') ? String(row[c.key]) : `₹${row[c.key]}`}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
